import { FC, ReactElement } from 'react';
import clsx from 'clsx';
import styles, { getTagTextColor } from './Tag.style';
import { TagColor } from 'types/enums';
import { TAG_TESTIDS } from './constants';
import { Spinner } from 'components/common/Spinner/Spinner';

interface TagProps {
    label: string;
    type: TagColor;
    contrast?: boolean;
    icon?: ReactElement;
    actionIcon?: ReactElement;
    onClick?: () => void;
    disabled?: boolean;
    isLoading?: boolean;
}

// An object to map and get the correct classname for each TagColor, is used in dynamicClass further down in the code
const colorClassMap: Record<TagColor, string> = {
    [TagColor.RED]: 'red',
    [TagColor.GREEN]: 'green',
    [TagColor.BLUE]: 'blue',
    [TagColor.YELLOW]: 'yellow',
    [TagColor.GREY]: 'grey',
    [TagColor.WHITE]: 'white',
};

export const Tag: FC<TagProps> = ({
    label,
    type,
    contrast,
    icon,
    actionIcon,
    onClick,
    disabled,
    isLoading,
}) => {
    const hasOnClickFunction = !!onClick && !disabled;
    const hasLeftIcon = !!icon;
    const hasRightIcon = !!actionIcon;
    const classes = styles({ hasOnClickFunction, hasLeftIcon, hasRightIcon });

    // Finds the color based on the passed in 'type' prop and sets correct class, e.g: if type = TagColor.BLUE and contrast = true
    // the dynamicClass = blueContrast
    const dynamicClass =
        `${colorClassMap[type]}${contrast ? 'Contrast' : ''}` as keyof typeof classes;

    // Get the text color for the spinner
    const spinnerColor = getTagTextColor(type, contrast, disabled);

    return (
        <div
            className={clsx(
                classes.container,
                classes[dynamicClass],
                hasOnClickFunction ? classes.clickable : classes.nonClickable,
                { [classes.disabled]: disabled },
            )}
            data-testid={`${TAG_TESTIDS.TAG}-${type}`}
            onClick={hasOnClickFunction ? onClick : undefined}
        >
            {icon}
            {label}
            {!isLoading && actionIcon}
            {isLoading && (
                <div
                    className={classes.tagLoadingContainer}
                    data-testid={TAG_TESTIDS.TAG_LOADING_CONTAINER}
                >
                    <Spinner size={8} fill={spinnerColor} />
                </div>
            )}
        </div>
    );
};
